import React from 'react'
import { IconButton } from './Icon'

// InfoTip is an ⓘ that opens a small bubble saying what something is. The
// bubble hangs under the ⓘ's wrapper, pointing up at it; `align` says which
// edge it lines up with, so it grows away from the screen edge the ⓘ sits
// near: "end" grows leftwards, "start" rightwards. If it would still run off
// the screen, it slides back on and its pointer slides the other way, so it
// keeps pointing at the ⓘ. Tapping the ⓘ again, tapping anywhere else, or
// Escape closes it; Escape hands focus back.
export function InfoTip({ title, lines, align = 'end', className }: {
  title: string
  lines: readonly string[]
  align?: 'start' | 'end'
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  const wrapRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const bubbleRef = React.useRef<HTMLDivElement>(null)
  const bubbleId = React.useId()
  const titleId = React.useId()

  // shift is how far the bubble slides left to stay 16px inside the screen
  // (negative slides it right).
  React.useLayoutEffect(() => {
    const bubble = bubbleRef.current
    if (!open || !bubble) return
    bubble.style.removeProperty('--info-tip-shift')
    const rect = bubble.getBoundingClientRect()
    const edge = 16
    const right = document.documentElement.clientWidth - edge
    let shift = 0
    if (rect.right > right) shift = rect.right - right
    if (rect.left - shift < edge) shift = rect.left - edge
    if (shift !== 0) bubble.style.setProperty('--info-tip-shift', `${Math.round(shift)}px`)
  }, [open])

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className={['info-tip', `info-tip--${align}`, className ?? ''].filter(Boolean).join(' ')} ref={wrapRef}>
      <IconButton
        ref={buttonRef}
        icon="info"
        label={title}
        className="info-tip__button"
        aria-expanded={open}
        aria-controls={bubbleId}
        onClick={() => setOpen((value) => !value)}
      />
      <div ref={bubbleRef} id={bubbleId} className="info-tip__bubble" role="note" aria-labelledby={titleId} hidden={!open}>
        <p className="info-tip__title" id={titleId}>{title}</p>
        {lines.map((line) => (
          <p key={line} className="info-tip__text">{line}</p>
        ))}
      </div>
    </div>
  )
}
